import React, { useId, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { CLEAR_REASONS, ClearReason } from '../tasks/taskActions';
import { INK } from '../ui';

/**
 * Clearing a task inline, in place of the old dashboard's "clear alerts" modal:
 * the usual reason is already picked, a note appears only when asked for (or
 * for "Other", where it is the only explanation), then confirm.
 */
export const ClearForm: React.FC<{
  taskTitle: string;
  onClear: (reason: ClearReason, note: string) => void;
  onCancel: () => void;
}> = ({ taskTitle, onClear, onCancel }) => {
  const [reason, setReason] = useState<ClearReason>(CLEAR_REASONS[0]);
  const [note, setNote] = useState('');
  const [noting, setNoting] = useState(false);
  const needsNote = reason === 'Other' && !note.trim();
  const showNote = noting || reason === 'Other';
  const questionId = useId();

  return (
    <Box
      component="form"
      aria-label={`Clear ${taskTitle}`}
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        if (!needsNote) onClear(reason, note);
      }}
      sx={{ position: 'relative', zIndex: 1, mt: 1.25, p: 2, borderRadius: '10px', bgcolor: '#F5F7FA', border: `1px solid ${INK.hairline}` }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Clear this task?</Typography>
      <Typography id={questionId} sx={{ fontSize: 13, color: INK.muted, mt: 0.25, mb: 1.25 }}>
        It leaves the list for everyone. Why is it being cleared?
      </Typography>
      <Box role="group" aria-labelledby={questionId} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {CLEAR_REASONS.map((option) => {
          const on = option === reason;
          return (
            <Button
              key={option}
              aria-pressed={on}
              autoFocus={on && option === CLEAR_REASONS[0]}
              size="small"
              onClick={() => setReason(option)}
              sx={{
                borderRadius: 999,
                px: 1.5,
                minHeight: 32,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${on ? INK.main : '#D5DCE5'}`,
                bgcolor: on ? INK.main : '#FFFFFF',
                color: on ? '#F8FAFC' : INK.body,
                '&:hover': { bgcolor: on ? INK.main : '#F1F4F8' },
              }}
            >
              {option}
            </Button>
          );
        })}
      </Box>
      {showNote ? (
        <TextField
          size="small"
          fullWidth
          autoFocus={noting && reason !== 'Other'}
          label={reason === 'Other' ? 'What happened?' : 'Note for the team'}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          required={reason === 'Other'}
          sx={{ mt: 1.5, bgcolor: '#FFFFFF' }}
        />
      ) : (
        <Button size="small" onClick={() => setNoting(true)} sx={{ mt: 1, ml: -0.75, textTransform: 'none', fontWeight: 500 }}>
          Add a note
        </Button>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Button type="submit" variant="contained" disableElevation disabled={needsNote} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
          Clear task
        </Button>
        <Button onClick={onCancel} sx={{ color: INK.body, textTransform: 'none', fontWeight: 500 }}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
